import React from 'react'
import EnhancedMeta from '../components/meta/enhanced-meta'
import Layout from '../components/layout/layout'
import { graphql } from 'gatsby'
import { generateServiceSchema, generateFAQSchema, generateBreadcrumbSchema } from '../utils/schema-generator'
import Parallax from '../components/parallax'
import ServiceTable from '../components/service-table'
import GoogleMapsEmbed from '../components/google-maps-embed'
import { Treatment, Question } from '../interfeces'
import RelatedTreatments from '../components/related-treatments'

interface Props {
  data: any
  location: Location
}

const treatment: Treatment = {
  duration: '45-60 minutes',
  session: '1 session',
  results: 'Results 9-12 months',
  cost: 'From £149.99',
  downtime: 'Minimal',
}

const relatedTreatments = [
  {
    name: 'Dermal Fillers Cheshunt',
    slug: 'dermal-fillers-cheshunt',
    description:
      'Discover how full-face dermal fillers can complement cheek contouring and volume restoration.',
  },
  {
    name: 'Tear Trough Fillers Cheshunt',
    slug: 'tear-trough-filer',
    description:
      'Brighten tired-looking eyes by pairing cheek lift with under-eye tear trough filler.',
  },
  {
    name: 'Lip Fillers Cheshunt',
    slug: 'lip-fillers',
    description:
      'Complete your facial profile with soft, natural-looking lip enhancement.',
  },
]

const questions: Array<Question> = [
  {
    question: 'How much do cheek fillers cost in Cheshunt?',
    answer:
      'Cheek filler treatments at Ada Aesthetics start from £149.99 per area using 1.1ml of premium Revolax filler. We also offer package deals: 4ml package (cheeks, jawline, chin) for £549.99. The final cost depends on the amount of product needed and your desired outcome. Book a free consultation to discuss your facial goals and receive a personalized quote.',
  },
  {
    question: 'How does this treatment work?',
    answer:
      'Fillers in this area are beneficial for many effects. We can create more defined, high cheekbones, making a face look slimmer and more modelled. As we age, this area can lose volume, creating indentation lines (folds). By adding volume in the cheek, we can pull this area up, making the line notably less visible and adding youthful volume.',
  },
  {
    question: 'Who is this treatment good for?',
    answer:
      'Cheek fillers at Ada Aesthetics are suitable for most ages as they have a wide range of uses. Great for anyone who prefers a more contoured look or those who would like to lift the mid areas of the face. Perfect for restoring volume lost through aging or creating definition.',
  },
  {
    question: 'Does it hurt?',
    answer:
      'The cheek area is not very sensitive at all, so you should be comfortable during the treatment. The fillers we use also contain anaesthetic which will numb the area during the procedure, ensuring maximum comfort.',
  },
  {
    question: 'How long do cheek fillers last?',
    answer:
      'Cheek fillers typically last 9-12 months, sometimes longer. Because the cheek area experiences less movement than lips, results tend to be longer-lasting. We recommend follow-up appointments to maintain your sculpted look.',
  },
  {
    question: 'Can cheek fillers help with sagging skin?',
    answer:
      'Yes! Cheek fillers at Ada Aesthetics can provide a subtle lift to the mid-face, which can reduce the appearance of sagging and nasolabial folds. By restoring volume to the cheeks, we create a more youthful facial structure.',
  },
  {
    question: 'Will I look overdone or unnatural?',
    answer:
      'We pride ourselves on natural-looking results. We carefully assess your facial structure and use precise injection techniques to enhance your natural beauty, not change it. The goal is subtle, elegant definition.',
  },
  {
    question: 'What is the difference between cheek fillers and chin fillers?',
    answer:
      'Cheek fillers at Ada Aesthetics add volume and definition to the mid-face, creating lift and contour. Chin fillers add projection and definition to the chin and jawline. Both can be combined for complete facial balance and profile enhancement.',
  },
]

const CheekFillers: React.FC<Props> = ({ data, location }: Props) => {
  const meta = data.site?.meta

  const serviceSchema = generateServiceSchema(
    'Cheek Fillers in Cheshunt',
    'Professional cheek filler treatments in Cheshunt, Hertfordshire. Restore volume, create definition, and lift the mid-face with premium hyaluronic acid fillers. Expert facial contouring.',
    '220'
  )

  const faqSchema = generateFAQSchema(questions.map(q => ({
    question: q.question,
    answer: q.answer
  })))

  const breadcrumbSchema = generateBreadcrumbSchema([
    { name: 'Home', url: 'https://adaaesthetics.uk' },
    { name: 'Treatments', url: 'https://adaaesthetics.uk/treatments' },
    { name: 'Cheek Fillers Cheshunt', url: 'https://adaaesthetics.uk/cheek-fillers' }
  ])

  const combinedSchema = {
    "@context": "https://schema.org",
    "@graph": [serviceSchema, faqSchema, breadcrumbSchema]
  }

  return (
    <Layout location={location}>
      <EnhancedMeta
        title="Cheek Fillers Cheshunt | Defined Cheekbones | Ada Aesthetics"
        description="Expert cheek filler treatments in Cheshunt. Create definition, lift mid-face & restore youthful volume. Premium dermal fillers for natural results. Book: 07478705252"
        keywords="cheek fillers Cheshunt, facial contouring Hertfordshire, cheek enhancement, mid-face lift, facial volume restoration Cheshunt"
        url="https://adaaesthetics.uk/cheek-fillers"
        schemaData={combinedSchema}
        site={meta}
      />
      <Parallax titleName="Cheek Fillers" customClass="page_title" />

      <section className="ls service-single s-pt-70 s-pb-20 s-pb-sm-50 s-py-lg-100 s-pt-xl-150 s-pb-xl-100 c-gutter-60 c-mb-50">
        <div className="container">
          <div className="row">
            <div className="col-12">
              <h1 className="special-heading">Sculpt Stunning Cheekbones with Cheek Fillers</h1>
              <p style={{ fontSize: '14px', color: '#666', marginTop: '-10px', marginBottom: '20px' }}>
                Expert facial contouring in Cheshunt, Hertfordshire
              </p>
              <p className="excerpt">
                Missing that youthful fullness your cheeks once had? Cheek fillers at Ada Aesthetics restore gorgeous volume, create elegant definition, and deliver a natural mid-face lift that takes years off your appearance. Watch as hollows disappear and beautiful cheekbone structure emerges—all without surgery. Conveniently located in Cheshunt, serving Waltham Cross, Broxbourne, and surrounding Hertfordshire areas.
              </p>
            </div>
          </div>

          <div className="row">
            <div className="col-12">
              <h2>What Are Cheek Fillers?</h2>
              <p>
                High cheekbones age well. Cheek fillers at Ada Aesthetics are excellent at defining
                the cheekbone or lifting the mid face as part of facial rejuvenation.
              </p>
              <p>
                This procedure also softens nose to mouth lines and gives a more rested, glowing look.
              </p>
              <p>
                We offer premium dermal fillers to add definition, contour and proportion
                to your whole face. Using hyaluronic acid-based fillers, we can restore lost volume, create stunning
                cheekbone definition, and provide a subtle lift to the entire mid-face.
              </p>

              <div className="cta-box" style={{
                background: '#f8f9fa',
                padding: '20px',
                borderLeft: '4px solid #d4af37',
                margin: '30px 0'
              }}>
                <p style={{ marginBottom: '10px', fontWeight: 'bold' }}>
                  Ready to sculpt stunning cheekbones?
                </p>
                <p style={{ marginBottom: '0' }}>
                  Call <a href="tel:07478705252" style={{ color: '#d4af37', fontWeight: 'bold', textDecoration: 'none' }}>07478 705252</a> for your free consultation
                </p>
              </div>

              <h2>Benefits of Cheek Fillers</h2>
              <p>Why choose cheek fillers at Ada Aesthetics?</p>
              <ul>
                <li><strong>Restore youthful volume</strong> lost through aging</li>
                <li><strong>Create defined cheekbones</strong> for a sculpted appearance</li>
                <li><strong>Lift the mid-face</strong> reducing sagging and jowls</li>
                <li><strong>Soften nasolabial folds</strong> (nose to mouth lines)</li>
                <li><strong>Balance facial proportions</strong> for harmony</li>
                <li><strong>Add subtle height</strong> to flat cheeks</li>
                <li><strong>Create a slimmer face</strong> through strategic contouring</li>
                <li><strong>Enhance overall facial aesthetics</strong> naturally</li>
                <li><strong>Long-lasting results</strong> (9-12 months)</li>
              </ul>

              <h2>How Do Cheek Fillers Work?</h2>
              <p>
                As we age, this area can lose volume, creating indentation lines (folds). By adding volume in the
                cheek at our Cheshunt clinic, we can pull this area up, making lines notably less visible and adding
                youthful volume.
              </p>
              <p>
                Cheek fillers work by injecting hyaluronic acid gel beneath the skin in precise locations to:
              </p>
              <ul>
                <li>Replace volume lost through natural aging</li>
                <li>Support and lift sagging skin in the mid-face</li>
                <li>Create structure and definition along the cheekbone</li>
                <li>Smooth the transition between cheek and lower face</li>
                <li>Hydrate and rejuvenate the skin from within</li>
              </ul>

              <h2>Types of Cheek Enhancement at Ada Aesthetics</h2>

              <h3>Cheek Filler</h3>
              <p>
                High cheekbones age well. Cheek fillers at our Cheshunt clinic are excellent at defining the
                cheekbone or lifting the mid face as part of facial rejuvenation. This procedure also softens
                nose to mouth lines and gives a more rested, glowing look.
              </p>

              <h3>Chin Enhancement</h3>
              <p>
                A weak chin can distort your profile. At Ada Aesthetics in Cheshunt, dermal fillers add definition
                to the chin area without the risk and downtime of surgery. Fillers are an excellent, safe alternative
                to permanent chin implants which avoids complications such as infection or movement.
              </p>

              <h3>Combined Approach</h3>
              <p>
                We often combine cheek and chin fillers at our Cheshunt clinic for complete facial balance. This
                creates harmony between the upper, mid, and lower face, resulting in beautifully proportioned features.
              </p>

              <h2>Your Cheek Filler Treatment Journey in Cheshunt</h2>

              <h3>1. Expert Consultation</h3>
              <p>
                Your journey begins with a comprehensive consultation at Ada Aesthetics in Cheshunt. We'll analyze
                your facial structure, discuss your aesthetic goals, and create a personalized treatment plan. Our
                approach focuses on natural enhancement that suits your unique features.
              </p>

              <h3>2. Precise Treatment</h3>
              <p>
                Cheek filler treatments take 45-60 minutes at our Cheshunt clinic. We use advanced injection
                techniques with ultra-fine needles or cannulas to place filler in precise locations along the
                cheekbone. The fillers contain local anaesthetic for your comfort.
              </p>

              <h3>3. Immediate Transformation</h3>
              <p>
                You'll see immediate improvement in cheek definition and mid-face volume. Some swelling is normal
                and typically subsides within 24-48 hours. Your final, beautifully contoured results will be visible
                within 2 weeks.
              </p>

              <h3>4. Long-Lasting Definition</h3>
              <p>
                Cheek fillers at our Cheshunt clinic last 9-12 months, often longer than lip fillers because the
                cheek area experiences less movement. We recommend maintenance appointments to preserve your
                sculpted, youthful appearance.
              </p>

              <h2>Who Is Suitable for Cheek Fillers?</h2>
              <p>Cheek fillers at Ada Aesthetics in Cheshunt are ideal if you:</p>
              <ul>
                <li>Have lost cheek volume due to aging</li>
                <li>Want more defined, prominent cheekbones</li>
                <li>Notice sagging in the mid-face area</li>
                <li>Have deep nasolabial folds (nose to mouth lines)</li>
                <li>Desire a more contoured, sculpted face</li>
                <li>Want to balance facial proportions</li>
                <li>Prefer non-surgical facial enhancement</li>
              </ul>

              <h2>Why Choose Ada Aesthetics for Cheek Fillers?</h2>
              <ul>
                <li><strong>Facial anatomy experts</strong> with advanced training in contouring</li>
                <li><strong>Premium hyaluronic acid products</strong> - safe and FDA-approved</li>
                <li><strong>Natural, balanced results</strong> - elegant enhancement</li>
                <li><strong>Artistic eye</strong> for facial proportions and symmetry</li>
                <li><strong>Advanced injection techniques</strong> for precision</li>
                <li><strong>Free consultations</strong> to discuss your goals</li>
                <li><strong>Conveniently located</strong> in Cheshunt, Hertfordshire</li>
                <li><strong>Serving local areas</strong> including Waltham Cross, Broxbourne, Hoddesdon</li>
                <li><strong>Competitive pricing</strong> with transparent costs</li>
              </ul>

              <div className="cta-box" style={{
                background: '#fff',
                padding: '25px',
                border: '2px solid #d4af37',
                margin: '40px 0',
                textAlign: 'center'
              }}>
                <h3 style={{ marginBottom: '15px', color: '#d4af37' }}>
                  Free Expert Consultation
                </h3>
                <p style={{ marginBottom: '15px', fontSize: '16px' }}>
                  Discuss your goals with our experienced practitioners
                </p>
                <a
                  href="tel:07478705252"
                  style={{
                    display: 'inline-block',
                    padding: '12px 30px',
                    background: '#d4af37',
                    color: '#fff',
                    textDecoration: 'none',
                    borderRadius: '4px',
                    fontWeight: 'bold'
                  }}
                >
                  Call 07478 705252
                </a>
              </div>

              <h2>Cheek Fillers vs Other Facial Treatments</h2>

              <h3>Cheek Fillers</h3>
              <ul>
                <li>Add volume and lift to mid-face</li>
                <li>Create cheekbone definition</li>
                <li>Results immediate and long-lasting (9-12 months)</li>
                <li>No surgery or downtime</li>
              </ul>

              <h3>Anti-Wrinkle Injections</h3>
              <ul>
                <li>Relax muscles to prevent wrinkles</li>
                <li>Best for forehead, crow's feet, frown lines</li>
                <li>Does not add volume</li>
              </ul>

              <h3>Thread Lifts</h3>
              <ul>
                <li>Surgical threads that lift skin</li>
                <li>More invasive with longer recovery</li>
                <li>May be combined with fillers</li>
              </ul>

              <p>
                At Ada Aesthetics, we often combine cheek fillers with anti-wrinkle injections for
                comprehensive facial rejuvenation.
              </p>
              <p>
                During your consultation, we'll recommend the best treatment plan for your goals.
              </p>

              <p style={{
                background: '#e8f5e9',
                padding: '15px',
                borderRadius: '4px',
                margin: '20px 0'
              }}>
                <strong>💚 Still have questions?</strong> Our friendly team is here to help.
                Call <strong>07478 705252</strong> to speak with a qualified practitioner.
              </p>

              <h2>Your Local Cheek Filler Clinic</h2>
              <p>
                Located at 15 Ousden Drive, Cheshunt, EN8 9RL, Ada Aesthetics is your local expert for cheek
                filler treatments and facial contouring.
              </p>
              <p>
                We're easily accessible for residents throughout
                Hertfordshire, including Waltham Cross, Broxbourne, Hoddesdon, Waltham Abbey, and Enfield.
              </p>
              <p>
                No need to travel to London for expert facial contouring - professional cheek enhancement with
                natural results is available locally at competitive prices.
              </p>

              <h2>Book Your Cheek Filler Consultation</h2>
              <p>
                Ready for beautifully defined cheekbones and a lifted, youthful appearance? Join 500+ clients who trust us
                for natural-looking facial contouring. Schedule your free consultation today - our experts will create a
                personalized treatment plan to enhance your natural beauty and restore youthful volume.
              </p>
              <p>
                Call us today at <strong>07478705252</strong> or book online to schedule your appointment.
                Your complimentary consultation (worth £50) includes facial structure analysis and custom treatment design.
              </p>

              <RelatedTreatments treatments={relatedTreatments} />

              <GoogleMapsEmbed
                embedUrl="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d2469.9642841486544!2d-0.04449842346989817!3d51.70251797183743!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x48761f5e6c8f7d0d%3A0x9f3e8f3e8f3e8f3e!2s15%20Ousden%20Drive%2C%20Cheshunt%2C%20Waltham%20Cross%20EN8%209RL!5e0!3m2!1sen!2suk!4v1234567890123"
                height={450}
                title="Ada Aesthetics Cheek Fillers Clinic Location in Cheshunt"
                loading="lazy"
              />
            </div>
          </div>

          <ServiceTable treatment={treatment} questions={questions} />
        </div>
      </section>
    </Layout>
  )
}

export default CheekFillers

export const pageQuery = graphql`
  query CheekFillers {
    site {
      meta: siteMetadata {
        title
        description
        siteUrl
        author
      }
    }
  }
`
